<script lang="ts">
  import { Link } from 'svelte-routing';
  import Chart from '../charts/Chart.svelte';
  import Dots from '../charts/Dots.svelte';
  import Readout from '../charts/Readout.svelte';
  import { api } from '../../api/client';
  import { climb, clockTime, length } from '../../utils/activities';
  import { DURATION_STEPS, addDays, dateTicks, daysBetween, niceTicks, polyline } from '../../utils/chart';
  import { today } from '../../utils/insights';
  import { weekStart } from '../../utils/progress';
  import { sportOf } from '../../utils/sports';
  import { effortName, type EffortRecord, type RecordActivity, type Records } from '../../utils/training';
  import { date, locale, month, monthDay, t } from '../../i18n/index.ts';

  // RecordsSection is Records on Progress (spec 006 FR-084, 006 FR-085, 006 FR-086, 006 FR-087, 006 FR-036):
  // sport chips, each best effort's 3 fastest opening their activities, a
  // distance's history of broken records, the longest distance, longest
  // moving time, and biggest climb, and how far back they reach. The
  // history is in the shared chart language (spec 010 FR-033): a point
  // at each record on a time axis running to today, where the last still
  // stands, and a record's button opens its activity.
  const COLOR = 'var(--theme-accent)';
  // MIN_DAYS keeps a history of a few weeks from filling the axis with
  // days, so its dates still read as dates.
  const MIN_DAYS = 28;
  const ws = weekStart(navigator.language);

  let sport = $state('');
  let r = $state<Records | null>(null);
  let open = $state<string | null>(null);
  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let error = $state('');

  $effect(() => {
    const s = sport;
    open = null;
    at = null;
    api.get<Records>(`/me/records${s ? `?sport=${s}` : ''}`)
      .then((got) => { r = got; error = ''; })
      .catch((e) => (error = e.message));
  });

  const day = (at: string) => date(new Date(at));

  const localDay = (iso: string) => new Date(iso).toLocaleDateString('en-CA');
  const yearFormat = new Intl.DateTimeFormat(locale(), { year: 'numeric' });

  // history lays out a distance's records by date from the first, or from
  // MIN_DAYS before today, to today, the time on the value scale so getting
  // faster reads as the line stepping down.
  function history(e: EffortRecord) {
    const hs = e.history;
    const to = today();
    let from = localDay(hs[0].started_at);
    if (daysBetween(from, to) < MIN_DAYS) from = addDays(to, -MIN_DAYS);
    const span = daysBetween(from, to);
    const points = hs.map((h) => ({ at: daysBetween(from, localDay(h.started_at)) / span, v: h.value, h }));
    const scale = niceTicks(Math.min(...hs.map((h) => h.value)), Math.max(...hs.map((h) => h.value)), { steps: DURATION_STEPS });
    const steps: [number, number][] = points.flatMap((p, i) => [[p.at, p.v], [points[i + 1]?.at ?? 1, p.v]] as [number, number][]);
    const ticks = dateTicks(from, to, 6, ws);
    const xTicks = ticks.days.map((d) => {
      const on = new Date(`${d}T12:00:00`);
      const text = ticks.unit === 'year' ? yearFormat.format(on) : ticks.unit === 'month' ? month(on, true) : monthDay(on);
      return { at: daysBetween(from, d) / span, text };
    });
    return { points, scale, line: polyline(steps, scale.lo, scale.hi), xTicks };
  }

  let others = $derived(r ? ([
    r.longest && { label: t('activitiesRecordsLongestDistance'), rec: r.longest, value: length(r.longest.value) },
    r.moving && { label: t('activitiesRecordsLongestTime'), rec: r.moving, value: clockTime(r.moving.value) },
    r.climb && { label: t('activitiesRecordsBiggestClimb'), rec: r.climb, value: climb(r.climb.value) },
  ].filter(Boolean) as { label: string; rec: RecordActivity; value: string }[]) : []);
</script>

<section class="card p-4 space-y-3" aria-labelledby="records-heading">
  <h2 id="records-heading" class="font-semibold">{t('activitiesRecords')}</h2>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if r}
    {#if r.sports.length === 0}
      <p class="text-sm text-text-muted">{t('activitiesRecordsEmpty')}</p>
    {:else}
      <div class="flex gap-2 overflow-x-auto pb-1" role="group" aria-label={t('activitiesSport')}>
        {#each r.sports as id (id)}
          <button class="btn btn-sm shrink-0 {r.sport === id ? 'btn-primary' : 'btn-secondary'}" aria-pressed={r.sport === id} onclick={() => (sport = id)}>
            <i aria-hidden="true" class="las la-{sportOf(id).icon} mr-1"></i>{sportOf(id).name}
          </button>
        {/each}
      </div>
      {#if r.efforts.length}
        <ul class="divide-y" style="border-color: var(--theme-surface0);">
          {#each r.efforts as e (e.key)}
            <li class="py-2">
              <button class="w-full flex items-baseline justify-between gap-2 text-left hover:opacity-80" aria-expanded={open === e.key}
                onclick={() => { open = open === e.key ? null : e.key; at = null; hover = null; }}>
                <span class="font-medium">{effortName(e.key)}</span>
                <span class="text-lg font-semibold">{clockTime(e.top[0].value)}<i aria-hidden="true" class="las {open === e.key ? 'la-angle-up' : 'la-angle-down'} ml-2 text-sm text-text-muted"></i></span>
              </button>
              <ol class="text-sm mt-1 space-y-0.5">
                {#each e.top as top, i (top.activity_id)}
                  <li>
                    <Link to="/activity/{top.activity_id}" class="flex gap-2 hover:opacity-80">
                      <span class="w-8 text-text-muted">{t('activitiesRecordsRank', { rank: i + 1 })}</span><span class="flex-1 min-w-0 truncate">{top.name}</span>
                      <span class="text-text-muted">{day(top.started_at)}</span><span class="w-16 text-right">{clockTime(top.value)}</span>
                    </Link>
                  </li>
                {/each}
              </ol>
              {#if open === e.key && e.history.length}
                {@const c = history(e)}
                {@const shown = hover ?? at}
                {@const h = shown != null ? e.history[shown] : null}
                <div class="mt-2 space-y-2">
                  <Readout title={h ? t('activitiesRecordsDateName', { date: day(h.started_at), name: h.name }) : t('activitiesRecordsBroken', { effort: effortName(e.key) })}
                    items={[{ label: h ? t('activitiesRecordsRecord') : t('activitiesRecordsRecordNow'), value: clockTime((h ?? e.history[e.history.length - 1]).value), color: COLOR }]}>
                    {#snippet action()}
                      {#if at != null}
                        <Link to="/activity/{e.history[at].activity_id}" class="btn btn-secondary btn-sm">{t('activitiesRecordsOpen')}</Link>
                      {/if}
                    {/snippet}
                  </Readout>
                  <Chart lo={c.scale.lo} hi={c.scale.hi} yTicks={c.scale.ticks.map((tick) => ({ v: tick, text: clockTime(tick) }))}
                    xTicks={c.xTicks} height="h-32" positions={c.points.map((p) => p.at)} bind:at bind:hover
                    label={t('activitiesRecordsHistory', { effort: effortName(e.key) })} valueText={h ? t('activitiesRecordsValueText', { date: day(h.started_at), name: h.name, time: clockTime(h.value) }) : t('activitiesRecordsNonePicked')}
                    dots={h ? [{ v: h.value, color: COLOR }] : []}>
                    {#snippet plot()}
                      <polyline points={c.line} fill="none" stroke={COLOR} stroke-width="2" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
                    {/snippet}
                    {#snippet overlay()}
                      <Dots dots={c.points} color={COLOR} lo={c.scale.lo} hi={c.scale.hi} />
                    {/snippet}
                  </Chart>
                  <table class="sr-only">
                    <caption>{t('activitiesRecordsHistory', { effort: effortName(e.key) })}</caption>
                    <thead><tr><th>{t('activitiesRecordsColDate')}</th><th>{t('activitiesRecordsColActivity')}</th><th>{t('activitiesRecordsColTime')}</th></tr></thead>
                    <tbody>
                      {#each e.history as h, i (i)}
                        <tr><td>{day(h.started_at)}</td><td>{h.name}</td><td>{clockTime(h.value)}</td></tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
      {#if others.length}
        <dl class="grid gap-3 sm:grid-cols-3">
          {#each others as o (o.label)}
            <div>
              <dt class="text-xs text-text-muted">{o.label}</dt>
              <dd><Link to="/activity/{o.rec.activity_id}" class="hover:opacity-80"><span class="text-lg font-semibold">{o.value}</span>
                <span class="block text-xs text-text-muted truncate">{t('activitiesRecordsNameDate', { name: o.rec.name, date: day(o.rec.started_at) })}</span></Link></dd>
            </div>
          {/each}
        </dl>
      {/if}
      {#if r.since}
        <p class="text-xs text-text-muted">{t('activitiesRecordsSince', { date: day(r.since) })}</p>
      {/if}
    {/if}
  {/if}
</section>
